import { DocumentDuplicateIcon, MagnifyingGlassIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions/global.config.type'
import { transactionTypeOptions } from 'config'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getConditionBrokerTemplates, getConditions, submitConditionBrokerTemplate } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Input2, Tooltip } from 'stories/components'
import { confirm } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'

import type { ICondition } from '../Conditions/types'
import { BrokerTemplateDetails } from './details'
import type { IConditionBrokerTemplate } from './types'

export function BrokerTemplates() {
  const { options: productTypeOptions, label: productTypeLabel } = useProductTypeOptions()
  const [query, setQuery] = useState('')
  const [conditions, setConditions] = useState<Array<ICondition>>([])
  const [templates, setTemplates] = useState<Array<IConditionBrokerTemplate>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const uwConditionCategories = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.UWConditionCategories] || [],
  )
  const uwCategories = useMemo(
    () => uwConditionCategories.map((item: any) => item.title),
    [uwConditionCategories],
  )

  useEffect(() => {
    refresh()
  }, [])

  const refresh = async () => {
    setIsLoading(true)
    const [{ data }, templates] = await Promise.all([getConditions(), getConditionBrokerTemplates()])
    setConditions(data)
    setTemplates(templates)
    setIsLoading(false)
  }

  if (isEditing)
    return (
      <BrokerTemplateDetails
        lastUpdatedAt={lastUpdatedAt}
        conditions={conditions}
        template={templates[selectedIndex]}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
        categories={uwCategories}
      />
    )

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
  }

  const onCopyTemplate = async (templateIndex: number) => {
    const target = templates[templateIndex]
    const options = templates
      .map((t, i) => ({ t, i }))
      .filter(({ i }) => i !== templateIndex)
      .map(({ t, i }) => ({
        name: `${t.productType} / ${t.transactionType}`,
        value: i,
      }))

    const sourceIndex: any = await confirmOptions('Choose template to copy from', options)
    if (sourceIndex === false) return

    const source = templates[sourceIndex]
    const content = (
      <div className="text-gray-600 mb-4 text-md">
        Are you sure you want to copy template from{' '}
        <strong>
          {source.productType} / {source.transactionType}
        </strong>{' '}
        to{' '}
        <strong>
          {target.productType} / {target.transactionType}
        </strong>
        ?
      </div>
    )
    const confirmed = await confirm(content)
    if (!confirmed) return

    setIsLoading(true)
    await submitConditionBrokerTemplate(target.id, source.conditions)
    await refresh()
    setIsLoading(false)
  }

  let index = 0

  return (
    <div className="Templates-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Loan Setup Condition Templates
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              className="w-96"
              onChange={(value) => setQuery(value)}
            />
          </div>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="px-3 py-3">
                Name (Product / Transaction)
              </th>
              <th scope="col" className="px-3">
                Conditions
              </th>
              <th scope="col" className="px-3">
                Required
              </th>
              <th scope="col" className="px-3">
                Application
              </th>
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            {productTypeOptions.map((productType, index1) =>
              transactionTypeOptions.map((transactionType, index2) => {
                if (!index1 && !index2) index = 0

                const productLabel = productTypeLabel(productType)
                if (
                  query &&
                  !productLabel.toLowerCase().includes(query.trim().toLowerCase()) &&
                  !productType.toLowerCase().includes(query.trim().toLowerCase()) &&
                  !transactionType.toLowerCase().includes(query.trim().toLowerCase())
                )
                  return null
                const no = index1 * transactionTypeOptions.length + index2
                const templateIndex = templates.findIndex(
                  (t) => t.productType == productType && t.transactionType == transactionType,
                )
                if (templateIndex == -1) return null

                index += 1
                return (
                  <tr className={`border-b ${index % 2 == 0 ? 'bg-slate-50' : 'bg-white'}`} key={`${no}`}>
                    <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {index}
                    </td>
                    <td className="px-3">
                      {productLabel} / {transactionType}
                    </td>
                    <td className="px-3">{templates[templateIndex].conditions.length}</td>
                    <td className="px-3">
                      {templates[templateIndex].conditions.filter((c) => c.required || c.brokerCategory == '0').length}
                    </td>
                    <td className="px-3">{templates[templateIndex].conditions.filter((c) => c.application).length}</td>

                    <td className="px-3">
                      <span className="flex gap-1">
                        <Tooltip message="Edit">
                          <span className="btn-icon" onClick={() => onEdit(templateIndex)}>
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>
                        </Tooltip>
                        <Tooltip message="Copy from other Template">
                          <span className="btn-icon" onClick={() => onCopyTemplate(templateIndex)}>
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </span>
                        </Tooltip>
                      </span>
                    </td>
                  </tr>
                )
              }),
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
